import api from '@/services/Api';
import { AxiosError, AxiosResponse } from 'axios';
import { ActionContext, Module } from 'vuex';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { errorSwal, questionSwal, successSwal } from '@/helper/swals';
import { SweetAlertResult } from 'sweetalert2';
import OffeneVerrechnung from '@/types/OffeneVerrechnung';
import ImpfDbLog from '@/types/ImpfDbLog';
import { RootState, StatistikState } from '@/store/types';
import { SystemEinstellung, SystemEinstellungKey } from '@/types/Einstellung';

export type ImpfstatistikOptions = {
    zeitraum: [string, string];
    jahrgang: [string, string];
    impfstoffe: number[];
};

export type ImpfstatistikOptionsPayload = {
    zeitraum: [string, string];
    jahrgang: [string, string];
    impfstoffe?: number[];
};

export type PatientImpfstatistikOptions = {
    zeitraum: [string, string];
    impfstoffe: number[];
    impfungMatch: '';
    impfaktion?: false;
    untersuchungen?: [];
    befundwerttexte?: [];
    befundvergleichsoperator?: number;
    befundwert?: number;
    exportUntersuchungen?: boolean;
};

export type PatientLeistungStatistikOptions = {
    zeitraum: [string, string];
    leistungen?: number[];
};

export type PatientStatistikOptions = {
    zeitraum: [string, string];
};

export type PatientImpfstatistikPayload = {
    zeitraum: [string, string];
    impfstoffe?: number[];
    impfungMatch: '';
    impfaktion?: false;
    untersuchungen?: [];
    befundwerttexte?: [];
    befundvergleichsoperator?: number;
    befundwert?: number;
};

export type PatientLeistungStatistikPayload = {
    zeitraum: [string, string];
    leistungen?: number[];
};

export type PatientStatistikPayload = {
    zeitraum: [string, string];
};

export type RechnungenOptions = {
    zeitraum: [string, string];
    zahlungsart: string;
    sortierung: string;
    offen: boolean;
    variante: any
};

export type ReisestatistikOptions = {
    zeitraum: [string, string];
    gruppierungAerzte: boolean;
};

export type StipasOptions = {
    zeitraum: [string, string];
};

export type TagesjournalOptions = {
    zeitpunkt: string;
    nachUhrzeit: boolean;
};

export type UmsatzstatistikOptions = {
    zeitraum: [string, string];
    gruppierungAerzte: boolean;
};

export type ImpfauswertungenOptions = {
    zeitraum: [string, string];
    variante: string;
    sortierung: string;
    sortierreihenfolge: string;
};

export type PatientenstatistikDetailiertOptions = {
    zeitraum: [string, string];
    variante: string;
    gruppierung: string;
};

export type PatientenstatistikStudentenOptions = {
    zeitraum: [string, string];
    gruppierung: string;
};

export type PreislisteOptions = {
    preisliste: string;
};

export type WavmOptions = {
    zeitraum: [string, string];
    alle: boolean;
};

export type SetStatistikDialogs = {
    impfung?: boolean;
    umsatz?: boolean;
    verrechnung?: boolean;
    rechnung?: boolean;
    reise?: boolean;
    patienten?: boolean;
    stipas?: boolean;
    wavm?: boolean;
    tagesjournal?: boolean;
    jahrgang?: boolean;
    salden?: boolean;
    patientenmitimpfung?: boolean;
    patientenmitleistungen?: boolean;
    patientenstatistik?: boolean;
    impfauswertung?: boolean;
    patientenstatistik_detailiert?: boolean;
    patientenstatistik_studenten?: boolean;
    preislisten?: boolean;
};

export type SetStatistikLoaders = {
    impfung?: boolean;
    patienten?: boolean;
    verrechnung?: boolean;
    jahrgang?: boolean;
};

export type SetStatistikUmsatzLoaders = {
    print?: boolean;
    export?: boolean;
};

export type SetStatistikRechnungLoaders = {
    print?: boolean;
    export?: boolean;
    exportFibu?: boolean;
};

export type SetStatistikOffeneSaldenLoaders = {
    print?: boolean;
};

export type SetStatistikPatientenLoaders = {
    print?: boolean;
    export?: boolean;
};

export type SetStatistikPatientenMitImpfungenLoaders = {
    export?: boolean;
};

export type SetStatistikPatientenMitLeistungenLoaders = {
    export?: boolean;
};

export type SetStatistikReiseLoaders = {
    print?: boolean;
    export?: boolean;
};

export type SetStatistikStipasLoaders = {
    print?: boolean;
    export?: boolean;
};

export type SetStatistikWavmLoaders = {
    export?: boolean;
    preview?: boolean;
    repeat?: boolean;
    logs?: boolean;
};

export type SetStatistikTagesjournalLoaders = {
    print?: boolean;
    export?: boolean;
};

export type setPatientenstatistikDetailiertLoaders = {
    print?: boolean;
    export?: boolean;
};

export type setPatientenstatistikStudentenLoaders = {
    print?: boolean;
    export?: boolean;
};

export type setPreislistenLoaders = {
    print?: boolean;
    export?: boolean;
};

type Context = ActionContext<StatistikState, RootState>;

export const statistik: Module<StatistikState, RootState> = {
    namespaced: true,
    state: {
        verrechnungen: [],
        wavmLogs: [],
        dialog: {
            impfung: false,
            umsatz: false,
            verrechnung: false,
            rechnung: false,
            salden: false,
            reise: false,
            patienten: false,
            stipas: false,
            wavm: false,
            tagesjournal: false,
            jahrgang: false,
            patientenmitimpfung: false,
            patientenmitleistungen: false,
            patientenstatistik: false,
            patientenstatistik_detailiert: false,
            preislisten: false,
        },
        loaders: {
            impfung: false,
            patienten: false,
            verrechnung: false,
            jahrgang: false,
            umsatz: {
                print: false,
                export: false,
            },
            rechnung: {
                print: false,
                export: false,
                exportFibu: false,
            },
            salden: {
                print: false,
            },
            patientenstatistik: {
                print: false,
                export: false,
            },
            reise: {
                print: false,
                export: false,
            },
            stipas: {
                print: false,
                export: false,
            },
            wavm: {
                export: false,
                preview: false,
                repeat: false,
                logs: false,
            },
            tagesjournal: {
                print: false,
                export: false,
            },
            patientenmitleistungen: {
                export: false,
            },
            patientenmitimpfungen: {
                export: false,
            },
            impfauswertung: {
                print: false,
            },
            patientenstatistik_detailiert: {
                print: false,
            },
            patientenstatistik_studenten: {
                print: false,
            },
            preislisten: {
                print: false,
            },
        },
    },
    actions: {
        exportExcel({ commit, rootState, rootGetters }: Context,
                    payload: { options: ImpfstatistikOptionsPayload }): void {
            commit('setLoaders', { impfung: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/impfstatistik/export', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    if (response.data.type === 'text/html') {
                        response.data.text().then((text: string) => {
                            const data = JSON.parse(text);
                            errorSwal('Impfstatistik', data.body);
                        });
                    } else {
                        const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                        const impfstelle = rootGetters['authentication/getImpfstelleName'].replace(/ /g, '_');
                        saveAs(response.data, `Impfstatistik_${date}.xlsx`);
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { impfung: false }));
        },
        exportPatientenMitImpfungenExcel({ commit, rootState, rootGetters }: Context,
                    payload: { options: PatientImpfstatistikPayload }): void {
            commit('SetPatientenMitImpfungenLoaders', { export: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/patientenmitimpfungen/export', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    if (response.data.type === 'text/html') {
                        response.data.text().then((text: string) => {
                            const data = JSON.parse(text);
                            errorSwal('Patienten mit Impfung', data.body);
                        });
                    } else {
                        const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                        const impfstelle = rootGetters['authentication/getImpfstelleName'].replace(/ /g, '_');
                        saveAs(response.data, `Patienten_mit_Impfung_${date}.xlsx`);
                    }
                })
                .catch(console.error)
                .finally(() => commit('SetPatientenMitImpfungenLoaders', { export: false }));
        },
        exportPatientenMitLeistungenExcel({ commit, rootState, rootGetters }: Context,
                                         payload: { options: PatientLeistungStatistikPayload }): void {
            commit('setPatientenMitLeistungenLoaders', { export: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/patientenmitleistungen/export', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    if (response.data.type === 'text/html') {
                        response.data.text().then((text: string) => {
                            const data = JSON.parse(text);
                            errorSwal('Patienten mit Leistungen', data.body);
                        });
                    } else {
                        const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                        const impfstelle = rootGetters['authentication/getImpfstelleName'].replace(/ /g, '_');
                        saveAs(response.data, `Patienten_mit_Leistungen_${date}.xlsx`);
                    }
                })
                .catch(console.error)
                .finally(() => commit('setPatientenMitLeistungenLoaders', { export: false }));
        },
        exportJahrgangsImpfstatistikExcel({ commit, rootState, rootGetters }: Context,
                                          payload: { options: ImpfstatistikOptionsPayload }): void {
            commit('setLoaders', { impfung: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/jahrgangsimpfstatistik/export', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    if (response.data.type === 'text/html') {
                        response.data.text().then((text: string) => {
                            const data = JSON.parse(text);
                            errorSwal('Impfstatistik', data.body);
                        });
                    } else {
                        const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                        const impfstelle = rootGetters['authentication/getImpfstelleName'].replace(/ /g, '_');
                        saveAs(response.data, `Jahrgangs-Impfstatistik_${date}.xlsx`);
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { impfung: false }));
        },
        printPatientenstatistik({ commit, dispatch, rootState }: Context, payload: { options: PatientStatistikPayload }): void {
            commit('setPatientenstatistikLoaders', { print: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/patientenstatistik', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => dispatch('globals/printDokument', { data }, { root: true }))
                .catch(console.error)
                .finally(() => commit('setPatientenstatistikLoaders', { print: false }));
        },
        exportPatientenstatistikExcel({ commit, rootState, rootGetters }: Context, payload: { options: PatientStatistikPayload }): void {
            commit('setPatientenstatistikLoaders', { excel: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/patientenstatistik/export', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    if (response.data.type === 'text/html') {
                        response.data.text().then((text: string) => {
                            const data = JSON.parse(text);
                            errorSwal('Patientenstatistik', data.body);
                        });
                    } else {
                        const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                        const impfstelle = rootGetters['authentication/getImpfstelleName'].replace(/ /g, '_');
                        saveAs(response.data, `Patientenstatistik_${date}.xlsx`);
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { excel: false }));
        },
        printReport({ commit, dispatch, rootState }: Context, payload: { options: UmsatzstatistikOptions }): void {
            commit('setUmsatzLoaders', { print: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/umsatzstatistik', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => dispatch('globals/printDokument', { data }, { root: true }))
                .catch(console.error)
                .finally(() => commit('setUmsatzLoaders', { print: false }));
        },
        printImpfauswertung({ commit, dispatch, rootState }: Context, payload: { options: ImpfauswertungenOptions }): void {
            commit('setImpfauswertungLoaders', { print: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/impfauswertung', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => dispatch('globals/printDokument', { data }, { root: true }))
                .catch(console.error)
                .finally(() => commit('setImpfauswertungLoaders', { print: false }));
        },
        printPatientenstatistikDetailiert({ commit, dispatch, rootState }: Context, payload: { options: PatientenstatistikDetailiertOptions }): void {
            commit('setPatientenstatistikDetailiertLoaders', { print: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/patientenstatistikdetailiert', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => dispatch('globals/printDokument', { data }, { root: true }))
                .catch(console.error)
                .finally(() => commit('setPatientenstatistikDetailiertLoaders', { print: false }));
        },
        printPatientenstatistikStudenten({ commit, dispatch, rootState }: Context, payload: { options: PatientenstatistikDetailiertOptions }): void {
            commit('setPatientenstatistikStudentenLoaders', { print: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/patientenstatistikstudenten', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => dispatch('globals/printDokument', { data }, { root: true }))
                .catch(console.error)
                .finally(() => commit('setPatientenstatistikStudentenLoaders', { print: false }));
        },
        printPreislisten({ commit, dispatch, rootState }: Context, payload: { options: PatientenstatistikDetailiertOptions }): void {
            commit('setPreislistenLoaders', { print: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/preislisten', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => dispatch('globals/printDokument', { data }, { root: true }))
                .catch(console.error)
                .finally(() => commit('setPreislistenLoaders', { print: false }));
        },
        exportReport({ commit, rootState, rootGetters }: Context, payload: { options: UmsatzstatistikOptions }): void {
            commit('setUmsatzLoaders', { export: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/umsatzstatistik/export', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                    const impfstelle = rootGetters['authentication/getImpfstelleName'].replace(/ /g, '_');
                    saveAs(response.data, `Umsatzstatistik_${date}.xlsx`);
                })
                .catch(console.error)
                .finally(() => commit('setUmsatzLoaders', { export: false }));
        },
        printOffeneSalden({ commit, dispatch, rootState }: Context, payload: []): void {
            commit('setOffeneSaldenLoaders', { print: true });

            api.post('statistik/offenesalden', { patienten: payload }, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => dispatch('globals/printDokument', { data }, { root: true }))
                .catch(console.error)
                .finally(() => commit('setOffeneSaldenLoaders', { print: false }));
        },
        printEinsendeformular({ commit, dispatch, rootState, rootGetters }: Context, payload: []): void {
            const getSystemEinstellung: <T>(parameter: SystemEinstellungKey) => SystemEinstellung<T> =
                rootGetters['authentication/getSystemEinstellung'];

            const checkTrays = String(getSystemEinstellung('etikettvariante').value) === 'MUG';

            api.post('statistik/einsendeformular', { data: payload }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    dispatch('globals/printDokument', { data: response.data, checkTrays}, { root: true });
                })
                .catch(console.error);
        },
        printRechnungen({ commit, dispatch, rootState }: Context, payload: { options: RechnungenOptions }): void {
            commit('setRechnungLoaders', { print: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/rechnungen', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => dispatch('globals/printDokument', { data }, { root: true }))
                .catch(console.error)
                .finally(() => commit('setRechnungLoaders', { print: false }));
        },
        exportRechnungen({ commit, rootState, rootGetters }: Context, payload: { options: RechnungenOptions }): void {
            commit('setRechnungLoaders', { export: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/rechnungen/export', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                    const zahlungsart = (payload.options.zahlungsart ?? '').replaceAll(' ', '_');
                    const impfstelle = rootGetters['authentication/getImpfstelleName'].replace(/ /g, '_');
                    saveAs(response.data, `Rechnungen_${zahlungsart}_${date}.xlsx`);
                })
                .catch(console.error)
                .finally(() => commit('setRechnungLoaders', { export: false }));
        },
        exportFibuRechnungen({ commit, rootState, rootGetters }: Context, payload: { options: RechnungenOptions }): void {
            commit('setRechnungLoaders', { exportFibu: true });

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload
            };

            const requests = [
                api.post('statistik/rechnungen/fibu', data, { responseType: 'blob' }),
                api.post('statistik/rechnungen/fibu', { sum: true, ...data }, { responseType: 'blob' }),
            ];

            Promise
                .all(requests)
                .then(([response, sumResponse]) => {
                    const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                    const impfstelle = rootGetters['authentication/getImpfstelleName'].replace(/ /g, '_');
                    saveAs(response.data, `FIBU_${date}.csv`);
                    saveAs(sumResponse.data, `FIBU_Summen_${date}.csv`);
                })
                .catch(console.error)
                .finally(() => commit('setRechnungLoaders', { exportFibu: false }));
        },
        printReiseReport({ commit, dispatch, rootState }: Context, payload: { options: ReisestatistikOptions }): void {
            commit('setReiseLoaders', { print: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/reisestatistik', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => dispatch('globals/printDokument', { data }, { root: true }))
                .catch(console.error)
                .finally(() => commit('setReiseLoaders', { print: false }));
        },
        exportReiseReport({ commit, rootState, rootGetters }: Context,
                          payload: { options: ReisestatistikOptions }): void {
            commit('setReiseLoaders', { export: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/reisestatistik/export', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                    const impfstelle = rootGetters['authentication/getImpfstelleName'].replace(/ /g, '_');
                    saveAs(response.data, `Reisestatistik_${date}.xlsx`);
                })
                .catch(console.error)
                .finally(() => commit('setReiseLoaders', { export: false }));
        },
        printStipas({ commit, dispatch, rootState }: Context, payload: { options: StipasOptions }): void {
            commit('setStipasLoaders', { print: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/stipas', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => dispatch('globals/printDokument', { data }, { root: true }))
                .catch(console.error)
                .finally(() => commit('setStipasLoaders', { print: false }));
        },
        exportStipas({ commit, rootState, rootGetters }: Context, payload: { options: StipasOptions }): void {
            commit('setStipasLoaders', { export: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/stipas/export', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                    const impfstelle = rootGetters['authentication/getImpfstelleName'].replace(/ /g, '_');
                    saveAs(response.data, `STIPAS_${date}.xlsx`);
                })
                .catch(console.error)
                .finally(() => commit('setStipasLoaders', { export: false }));
        },
        exportWavm({ commit, rootState }: Context, payload: { options: WavmOptions }): void {
            commit('setWavmLoaders', { export: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            const von = moment(payload.options.zeitraum[0], 'YYYY-MM-DD').format('DDMMYY');
            const bis = moment(payload.options.zeitraum[1], 'YYYY-MM-DD').format('DDMMYY');

            let question = 'Wollen Sie den FTP-Export wirklich durchführen?';
            if (payload.options.alle) {
                question = 'Wollen Sie den FTP-Export für ALLE Impfstellen wirklich durchführen?';
            }

            questionSwal('Exportieren', question, 'Exportieren', 'Abbrechen')
                .then(({ isConfirmed }: SweetAlertResult) => {
                    if (isConfirmed) {
                        api.post('wavm/export', { impfstelle_id, ...payload }, { responseType: 'blob' })
                            .then((response: AxiosResponse) => {
                                saveAs(response.data, `IVS${von}bis${bis}.txt`);
                                successSwal('WAVM-Export', 'übertragen');
                                commit('setDialogs', { wavm: false });
                            })
                            .catch((error: AxiosError) => {
                                console.error(error);
                                errorSwal('WAVM-Export', 'Fehler beim Übertragen');
                            })
                            .finally(() => commit('setWavmLoaders', { export: false }));
                    } else {
                        commit('setWavmLoaders', { export: false });
                    }
                });
        },
        repeatWavm({ commit, rootState }: Context, payload: { options: WavmOptions }): void {
            commit('setWavmLoaders', { repeat: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            const von = moment(payload.options.zeitraum[0], 'YYYY-MM-DD').format('DDMMYY');
            const bis = moment(payload.options.zeitraum[1], 'YYYY-MM-DD').format('DDMMYY');

            let question = 'Wollen Sie den FTP-Export erneut durchführen?';
            if (payload.options.alle) {
                question = 'Wollen Sie den FTP-Export für ALLE Impfstellen erneut durchführen?';
            }

            questionSwal('Exportieren', question, 'Exportieren', 'Abbrechen')
                .then(({ isConfirmed }: SweetAlertResult) => {
                    if (isConfirmed) {
                        api.post('wavm/export/repeat', { impfstelle_id, ...payload }, { responseType: 'blob' })
                            .then((response: AxiosResponse) => {
                                saveAs(response.data, `IVS${von}bis${bis}.txt`);
                                successSwal('WAVM-Export', 'übertragen');
                                commit('setDialogs', { wavm: false });
                            })
                            .catch((error: AxiosError) => {
                                console.error(error);
                                errorSwal('WAVM-Export', 'Fehler beim Übertragen');
                            })
                            .finally(() => commit('setWavmLoaders', { repeat: false }));
                    } else {
                        commit('setWavmLoaders', { repeat: false });
                    }
                });
        },
        previewWavm({ commit, rootState }: Context, payload: { options: WavmOptions }): void {
            commit('setWavmLoaders', { preview: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            const von = moment(payload.options.zeitraum[0], 'YYYY-MM-DD').format('DDMMYY');
            const bis = moment(payload.options.zeitraum[1], 'YYYY-MM-DD').format('DDMMYY');

            api.post('wavm/export/preview', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    saveAs(response.data, `Vorschau_IVS${von}bis${bis}.txt`);
                })
                .catch(console.error)
                .finally(() => commit('setWavmLoaders', { preview: false }));
        },
        loadWavmLogs({ commit, rootState }: Context): void {
            commit('setWavmLoaders', { logs: true });

            api.post('wavm/export/logs', { impfstelle_id: rootState.authentication.impfstelle_id })
                .then((response: AxiosResponse) => commit('setWavmLogs', response.data.body))
                .catch(console.error)
                .finally(() => commit('setWavmLoaders', { logs: false }));
        },
        printTagesjournal({ commit, dispatch, rootState }: Context, payload: { options: TagesjournalOptions }): void {
            commit('setTagesjournalLoaders', { print: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/tagesjournal', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => dispatch('globals/printDokument', { data }, { root: true }))
                .catch(console.error)
                .finally(() => commit('setTagesjournalLoaders', { print: false }));
        },
        exportTagesjournal({ commit, rootState, rootGetters }: Context,
                           payload: { options: TagesjournalOptions }): void {
            commit('setTagesjournalLoaders', { export: true });

            const date = moment(payload.options.zeitpunkt, 'YYYY-MM-DD').format('YYYY-MM-DD');
            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/tagesjournal/export', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    const impfstelle = rootGetters['authentication/getImpfstelleKurz'].replace(/ /g, '_');
                    saveAs(response.data, `Tagesjournal_${date}.xlsx`);
                })
                .catch(console.error)
                .finally(() => commit('setTagesjournalLoaders', { export: false }));
        },
        loadOffeneVerrechnung({ commit, rootState }: Context): void {
            commit('setLoaders', { verrechnung: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/offeneverrechnung/load', { impfstelle_id })
                .then((response: AxiosResponse) => commit('setOffeneVerrechnungen', response.data.body))
                .catch(console.error)
                .finally(() => commit('setLoaders', { verrechnung: false }));
        },
        exportOffeneVerrechnung({ commit, rootState, rootGetters }: Context):
            void {
            commit('setLoaders', { export: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('statistik/offeneverrechnung/export', { impfstelle_id }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                    const impfstelle = rootGetters['authentication/getImpfstelleName'].replace(/ /g, '_');
                    saveAs(response.data, `OffeneVerrechnung_${date}.xlsx`);
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { export: false }));
        },
        setDialogs({ commit }: Context, payload: SetStatistikDialogs): void {
            commit('setDialogs', payload);
        },
        setLoaders({ commit }: Context, payload: SetStatistikLoaders): void {
            commit('setLoaders', payload);
        },
    },
    mutations: {
        setOffeneVerrechnungen(state: StatistikState, verrechnungen: Partial<OffeneVerrechnung>[]): void {
            state.verrechnungen = verrechnungen.map((data) => new OffeneVerrechnung(data));
        },
        setWavmLogs(state: StatistikState, wavmLogs: Partial<ImpfDbLog>[]): void {
            state.wavmLogs = wavmLogs.map((data) => new ImpfDbLog(data));
        },
        setDialogs(state: StatistikState, dialog: SetStatistikDialogs): void {
            state.dialog = {
                ...state.dialog,
                ...dialog,
            };
        },
        setLoaders(state: StatistikState, loaders: SetStatistikLoaders): void {
            state.loaders = {
                ...state.loaders,
                ...loaders,
            };
        },
        setUmsatzLoaders(state: StatistikState, loaders: SetStatistikUmsatzLoaders): void {
            state.loaders.umsatz = {
                ...state.loaders.umsatz,
                ...loaders,
            };
        },
        setRechnungLoaders(state: StatistikState, loaders: SetStatistikRechnungLoaders): void {
            state.loaders.rechnung = {
                ...state.loaders.rechnung,
                ...loaders,
            };
        },
        setOffeneSaldenLoaders(state: StatistikState, loaders: SetStatistikOffeneSaldenLoaders): void {
            state.loaders.salden = {
                ...state.loaders.salden,
                ...loaders,
            };
        },
        setPatientenMitLeistungenLoaders(state: StatistikState, loaders: SetStatistikPatientenMitLeistungenLoaders): void {
            state.loaders.patientenmitleistungen = {
                ...state.loaders.patientenmitleistungen,
                ...loaders,
            };
        },
        setPatientenstatistikLoaders(state: StatistikState, loaders: SetStatistikPatientenLoaders): void {
            state.loaders.patientenstatistik = {
                ...state.loaders.patientenstatistik,
                ...loaders,
            };
        },
        SetPatientenMitImpfungenLoaders(state: StatistikState, loaders: SetStatistikPatientenMitImpfungenLoaders): void {
            state.loaders.patientenmitimpfungen = {
                ...state.loaders.patientenmitimpfungen,
                ...loaders,
            };
        },
        setReiseLoaders(state: StatistikState, loaders: SetStatistikReiseLoaders): void {
            state.loaders.reise = {
                ...state.loaders.reise,
                ...loaders,
            };
        },
        setStipasLoaders(state: StatistikState, loaders: SetStatistikStipasLoaders): void {
            state.loaders.stipas = {
                ...state.loaders.stipas,
                ...loaders,
            };
        },
        setWavmLoaders(state: StatistikState, loaders: SetStatistikWavmLoaders): void {
            state.loaders.wavm = {
                ...state.loaders.wavm,
                ...loaders,
            };
        },
        setTagesjournalLoaders(state: StatistikState, loaders: SetStatistikTagesjournalLoaders): void {
            state.loaders.tagesjournal = {
                ...state.loaders.tagesjournal,
                ...loaders,
            };
        },
        setImpfauswertungLoaders(state: StatistikState, loaders: SetStatistikTagesjournalLoaders): void {
            state.loaders.impfauswertung = {
                ...state.loaders.impfauswertung,
                ...loaders,
            };
        },
        setPatientenstatistikDetailiertLoaders(state: StatistikState, loaders: setPatientenstatistikDetailiertLoaders): void {
            state.loaders.patientenstatistik_detailiert = {
                ...state.loaders.patientenstatistik_detailiert,
                ...loaders,
            };
        },
        setPatientenstatistikStudentenLoaders(state: StatistikState, loaders: setPatientenstatistikDetailiertLoaders): void {
            state.loaders.patientenstatistik_studenten = {
                ...state.loaders.patientenstatistik_studenten,
                ...loaders,
            };
        },
        setPreislistenLoaders(state: StatistikState, loaders: setPatientenstatistikDetailiertLoaders): void {
            state.loaders.preislisten = {
                ...state.loaders.preislisten,
                ...loaders,
            };
        },
    },
};
